import * as React from "react";

import { cn } from "../lib/utils";
import { Button } from "./button";
import { ColorPanel, isTransparent, pushRecentColor } from "./color-picker";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

/**
 * SIM-1185: when the resolved color is `transparent`, the swatch bar would
 * paint as nothing (a blank 2px stripe). Show the same checkerboard pattern
 * used by the Transparent preset so users can see at-a-glance that the
 * current value is "no fill".
 */
const TRANSPARENT_SWATCH_STYLE: React.CSSProperties = {
  backgroundImage:
    "linear-gradient(45deg, #c0c0c0 25%, transparent 25%), " +
    "linear-gradient(-45deg, #c0c0c0 25%, transparent 25%), " +
    "linear-gradient(45deg, transparent 75%, #c0c0c0 75%), " +
    "linear-gradient(-45deg, transparent 75%, #c0c0c0 75%)",
  backgroundSize: "4px 4px",
  backgroundPosition: "0 0, 0 2px, 2px -2px, -2px 0",
  backgroundColor: "#ffffff",
};

type ColorButtonProps = {
  /** Persisted user override. `undefined` means "use the default". */
  value: string | undefined;
  /** Emit `hex` when the user picks a color; emit `undefined` to clear. */
  onChange: (hex: string | undefined) => void;
  /** Icon rendered inside the trigger button (e.g. `A` letter or paint bucket). */
  icon: React.ReactNode;
  /** Required for screen-reader users — describes the picker's purpose. */
  "aria-label": string;
  /**
   * SIM-1165 no-lying defaults: the resolved color shown in the swatch / bar.
   * Defaults to `value` when omitted. Pass the rendered fallback (e.g.
   * `value ?? TABLE_HEADER_BG_DEFAULT`) so the swatch always reflects what
   * the user sees in the live component.
   */
  resolvedValue?: string;
  /** Optional reset action — clears the override (emits `onChange(undefined)`). */
  onReset?: () => void;
  disabled?: boolean;
  className?: string;
};

/**
 * SIM-1165: icon-button opening a popover colour picker, used by the FontEditor font-colour and
 * table header-background controls. The trigger always shows the **resolved** colour — override or
 * default — so a user never sees a blank swatch and wonders what is rendering (the SIM-1120
 * no-lying-defaults rule); `transparent` shows a checkerboard (SIM-1185).
 */
function ColorButton({
  value,
  onChange,
  icon,
  "aria-label": ariaLabel,
  resolvedValue,
  disabled,
  className,
}: ColorButtonProps) {
  const swatchColor = resolvedValue ?? value ?? "#000000";
  const swatchIsTransparent = isTransparent(swatchColor);
  // Inside the popover the picker needs a non-empty string. When the user has
  // no override, fall back to the resolved color (or black) so the picker's
  // initial state matches what the renderer is painting.
  const pickerValue = value ?? resolvedValue ?? "#000000";
  return (
    <Popover onOpenChange={(open) => !open && pushRecentColor(pickerValue)}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn("h-9 w-9 p-0 flex flex-col items-center justify-center gap-0.5", className)}
        >
          <span className="flex h-4 w-4 items-center justify-center" aria-hidden>
            {icon}
          </span>
          <span
            data-testid="color-button-swatch"
            aria-hidden
            className="block h-1.5 w-4 rounded-sm"
            style={
              swatchIsTransparent ? TRANSPARENT_SWATCH_STYLE : { backgroundColor: swatchColor }
            }
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-3">
        <ColorPanel value={pickerValue} onChange={(hex) => onChange(hex)} disabled={disabled} />
      </PopoverContent>
    </Popover>
  );
}

export { ColorButton };
export type { ColorButtonProps };
